import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, Heart, Camera, Calendar, ArrowRight, Shield, Star, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Landing() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/stats`).then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const formatNumber = (n) => n?.toLocaleString() || '—';

  return (
    <div className="min-h-screen bg-[#F9F7F3]">
      <header className="border-b border-[#E5E0D8] bg-[#F9F7F3] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-light tracking-tight" style={{fontFamily: 'Spectral, serif'}}>Stable Stationary</h1>
          <nav className="flex items-center gap-4">
            <Link to="/about"><Button variant="ghost" data-testid="nav-about-btn">About</Button></Link>
            <Link to="/pricing"><Button variant="ghost" data-testid="nav-pricing-btn">Pricing</Button></Link>
            <Link to="/login"><Button variant="ghost" data-testid="nav-login-btn">Login</Button></Link>
            <Link to="/signup">
              <Button className="bg-[#B94A3E] hover:bg-[#9d3e33] text-white" data-testid="nav-signup-btn">Start Free Trial</Button>
            </Link>
          </nav>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#F5EBE0] border border-[#B94A3E]/20 rounded-full px-4 py-1.5 mb-6">
              <Heart className="w-4 h-4 text-[#B94A3E]" />
              <span className="text-xs label-text">7-day free trial · No credit card</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-light mb-6" style={{fontFamily: 'Spectral, serif'}}>
              Write more meaningful letters.<br/>Stay connected.
            </h1>
            <p className="text-base leading-relaxed text-[#1A1A1A]/85 mb-8 max-w-2xl">
              Write more meaningful letters, create memory-style photos, and stay connected with your incarcerated loved one through a simple monthly plan.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/signup">
                <Button className="bg-[#B94A3E] hover:bg-[#9d3e33] text-white px-8 py-6 text-lg" data-testid="hero-get-started-btn">
                  Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/about">
                <Button variant="outline" className="px-8 py-6 text-lg border-[#E5E0D8]" data-testid="hero-learn-more-btn">
                  Learn more
                </Button>
              </Link>
            </div>
            <p className="text-xs text-[#737373] mt-6">Write every day. Stay connected every week. Delivery timing depends on facility rules.</p>
          </div>
          <div className="relative">
            <img
              src="https://images.pexels.com/photos/5207601/pexels-photo-5207601.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Stack of vintage letters"
              className="w-full rounded-md shadow-[2px_2px_0px_rgba(26,26,26,0.08)]"
              data-testid="hero-image"
            />
          </div>
        </div>
      </section>

      {/* Social proof stats bar */}
      <section className="bg-[#1A1A1A] text-white py-12" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6">
          <p className="label-text text-center text-white/60 mb-8">Trusted by families across America</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-light mb-2" style={{fontFamily: 'Spectral, serif'}} data-testid="stat-families">
                {formatNumber(stats?.families_connected)}
              </p>
              <p className="text-xs uppercase tracking-widest text-white/60">Families Connected</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-light mb-2" style={{fontFamily: 'Spectral, serif'}} data-testid="stat-letters">
                {formatNumber(stats?.letters_written)}
              </p>
              <p className="text-xs uppercase tracking-widest text-white/60">Letters Written</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-light mb-2" style={{fontFamily: 'Spectral, serif'}} data-testid="stat-mailed">
                {formatNumber(stats?.letters_mailed)}
              </p>
              <p className="text-xs uppercase tracking-widest text-white/60">Letters Mailed</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-light mb-2" style={{fontFamily: 'Spectral, serif'}} data-testid="stat-active">
                {formatNumber(stats?.active_writers_30d)}
              </p>
              <p className="text-xs uppercase tracking-widest text-white/60">Active This Month</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-[#E5E0D8] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-normal text-center mb-16" style={{fontFamily: 'Spectral, serif'}}>
            How It Works
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center" data-testid="feature-card-1">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#E5E0D8] flex items-center justify-center">
                <Heart className="w-8 h-8 text-[#B94A3E]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl mb-2" style={{fontFamily: 'Spectral, serif'}}>Tell Us About You</h3>
              <p className="text-sm text-[#737373]">Share your writing style and relationship so letters feel authentic.</p>
            </div>
            <div className="text-center" data-testid="feature-card-2">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#E5E0D8] flex items-center justify-center">
                <Mail className="w-8 h-8 text-[#B94A3E]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl mb-2" style={{fontFamily: 'Spectral, serif'}}>AI Drafts Letters</h3>
              <p className="text-sm text-[#737373]">Choose the tone. AI writes personalized letters you can edit before sending.</p>
            </div>
            <div className="text-center" data-testid="feature-card-3">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#E5E0D8] flex items-center justify-center">
                <Camera className="w-8 h-8 text-[#B94A3E]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl mb-2" style={{fontFamily: 'Spectral, serif'}}>Memory Photos</h3>
              <p className="text-sm text-[#737373]">Create AI memory-style photos to include. Every AI photo is clearly labeled.</p>
            </div>
            <div className="text-center" data-testid="feature-card-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#E5E0D8] flex items-center justify-center">
                <Calendar className="w-8 h-8 text-[#B94A3E]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl mb-2" style={{fontFamily: 'Spectral, serif'}}>Print & Mail</h3>
              <p className="text-sm text-[#737373]">We print and mail your letters. Track your consistency on the calendar.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="testimonials-section">
        <p className="label-text text-center mb-4">What people say</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-normal text-center mb-16" style={{fontFamily: 'Spectral, serif'}}>
          Real families. Real letters. Real connection.
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { quote: "Every Sunday I sit down and it just... works. I used to feel guilty for going weeks without writing. Now my husband gets a letter every single week.", name: "Maria T.", context: "Wife · San Antonio, TX" },
            { quote: "The AI helps me find the words. I always knew what I wanted to say to my son but couldn't get it out on paper. This gave me my voice back.", name: "Diane P.", context: "Mother · Cleveland, OH" },
            { quote: "The memory photos alone are worth it. I sent one of us at the beach — a place we always talked about going. He wrote back crying happy tears.", name: "James R.", context: "Brother · Atlanta, GA" }
          ].map((t, i) => (
            <div key={i} className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]" data-testid={`testimonial-${i}`}>
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#B94A3E] text-[#B94A3E]" />)}
              </div>
              <p className="text-[#1A1A1A]/85 leading-relaxed mb-6 italic" style={{fontFamily: 'Spectral, serif'}}>"{t.quote}"</p>
              <div>
                <p className="font-medium">{t.name}</p>
                <p className="text-xs text-[#737373]">{t.context}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border-t border-[#E5E0D8] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/3571342/pexels-photo-3571342.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Connection and support"
                className="w-full rounded-md shadow-[2px_2px_0px_rgba(26,26,26,0.08)]"
              />
            </div>
            <div>
              <p className="label-text mb-4">Backed by research</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-normal mb-6" style={{fontFamily: 'Spectral, serif'}}>
                Mail changes lives inside.
              </h2>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="w-16 flex-shrink-0">
                    <p className="text-3xl font-light text-[#B94A3E]" style={{fontFamily: 'Spectral, serif'}}>39%</p>
                  </div>
                  <p className="text-sm text-[#1A1A1A]/85">Lower recidivism rate for those with consistent family contact during incarceration</p>
                </div>
                <div className="flex gap-4">
                  <div className="w-16 flex-shrink-0">
                    <p className="text-3xl font-light text-[#B94A3E]" style={{fontFamily: 'Spectral, serif'}}>13x</p>
                  </div>
                  <p className="text-sm text-[#1A1A1A]/85">Higher rate of maintaining family ties when regular mail is received</p>
                </div>
                <div className="flex gap-4">
                  <div className="w-16 flex-shrink-0">
                    <p className="text-3xl font-light text-[#B94A3E]" style={{fontFamily: 'Spectral, serif'}}>2.7M</p>
                  </div>
                  <p className="text-sm text-[#1A1A1A]/85">Children with an incarcerated parent in the United States alone</p>
                </div>
              </div>
              <Link to="/about" className="inline-flex items-center gap-2 mt-6 text-[#B94A3E] hover:underline text-sm">
                Read our mission <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-[#F9F7F3] border-t border-[#E5E0D8] py-10" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-6 text-center">
            <div className="flex items-center gap-3 justify-center">
              <Shield className="w-5 h-5 text-[#B94A3E]" strokeWidth={1.5} />
              <span className="text-sm">Secure & Private</span>
            </div>
            <div className="flex items-center gap-3 justify-center">
              <Sparkles className="w-5 h-5 text-[#B94A3E]" strokeWidth={1.5} />
              <span className="text-sm">AI clearly labeled</span>
            </div>
            <div className="flex items-center gap-3 justify-center">
              <Mail className="w-5 h-5 text-[#B94A3E]" strokeWidth={1.5} />
              <span className="text-sm">USPS-compliant mailing</span>
            </div>
            <div className="flex items-center gap-3 justify-center">
              <Heart className="w-5 h-5 text-[#B94A3E]" strokeWidth={1.5} />
              <span className="text-sm">Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="bg-[#F5EBE0] border border-[#B94A3E]/20 rounded-md p-12 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight leading-tight font-normal mb-4" style={{fontFamily: 'Spectral, serif'}}>
            Someone inside is waiting for mail.
          </h2>
          <p className="text-lg text-[#1A1A1A]/85 mb-8 max-w-2xl mx-auto">
            Start free for 7 days. Write two letters. Send four memory photos. See what showing up feels like.
          </p>
          <Link to="/signup">
            <Button className="bg-[#B94A3E] hover:bg-[#9d3e33] text-white px-8 py-6 text-lg" data-testid="final-cta-btn">
              Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          <p className="text-xs text-[#737373] mt-4">No credit card required · Cancel anytime</p>
        </div>
      </section>

      <footer className="border-t border-[#E5E0D8] bg-[#F9F7F3] py-12">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-8">
          <div>
            <h3 className="text-lg mb-3" style={{fontFamily: 'Spectral, serif'}}>Stable Stationary</h3>
            <p className="text-xs text-[#737373]">Helping families show up, one letter at a time.</p>
          </div>
          <div>
            <p className="text-xs label-text mb-3">Product</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/pricing" className="text-[#1A1A1A]/70 hover:text-[#B94A3E]">Pricing</Link></li>
              <li><Link to="/about" className="text-[#1A1A1A]/70 hover:text-[#B94A3E]">About</Link></li>
              <li><Link to="/signup" className="text-[#1A1A1A]/70 hover:text-[#B94A3E]">Start Free</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs label-text mb-3">Trust</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/compliance" className="text-[#1A1A1A]/70 hover:text-[#B94A3E]">Community Standards</Link></li>
              <li><Link to="/compliance" className="text-[#1A1A1A]/70 hover:text-[#B94A3E]">Privacy</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs label-text mb-3">Contact</p>
            <ul className="space-y-2 text-sm">
              <li><a href="mailto:hello@stablestationary.com" className="text-[#1A1A1A]/70 hover:text-[#B94A3E]">hello@stablestationary.com</a></li>
              <li><a href="mailto:press@stablestationary.com" className="text-[#1A1A1A]/70 hover:text-[#B94A3E]">Press inquiries</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 text-center mt-12 pt-8 border-t border-[#E5E0D8]">
          <p className="text-sm text-[#737373]">© 2026 Stable Stationary. All rights reserved.</p>
          <p className="text-xs text-[#737373] mt-2">Delivery timing depends on facility rules. AI photos are clearly labeled.</p>
        </div>
      </footer>
    </div>
  );
}
